import {
  Component, inject,
  Input, OnChanges, OnDestroy,
  ViewChild,
} from '@angular/core';
import {ConceptsContainer} from '../../../core/definitions/concepts-container';
import {ConceptField} from '../../admin-concept-lists/concept-field';
import {ConceptService} from '../../../core/concept.service';
// @ts-ignore
import {TableColumn} from '../../../shared/primus-tables/primus-table/primus-table.component';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {Subscription} from 'rxjs';
import {Concept} from '../../../core/definitions/concepts';
import {CmsApiService} from "../../../core/cms-api.service";
import {MetaField} from "../../../core/definitions/meta-field";
import {FieldType} from "../../../core/definitions/field-type.enum";
import {map} from 'rxjs/operators';
import {BreakpointObserver} from '@angular/cdk/layout';
import {toSignal} from '@angular/core/rxjs-interop';
import {AConst} from "../../../core/a-const.enum";
import {FeatureFlagsService} from "../../../core/feature-flags.service";


@Component({
    selector: 'app-admin-list-table',
    templateUrl: './admin-list-table.component.html',
    styleUrls: ['./admin-list-table.component.scss'],
    standalone: false
})
export class AdminListTableComponent implements OnChanges, OnDestroy {
  // Injections
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly conceptService: ConceptService = inject(ConceptService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);

  @Input() conceptsContainer: ConceptsContainer;
  @Input() reloadConcepts;
  nameField = 'name';

  private savePageIndex: number;

  /**
   * Sets up pagination when the MatPaginator-component is loaded
   * @param {MatPaginator} paginator
   */
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    this.conceptsContainer.conceptDataSource.paginator = paginator;
    paginator.pageIndex = this.conceptsContainer.pageIndex;
    this.setupSortChange();
  }

  /**
   * Sets up sorting when the MatSort-directive is loaded
   * @param {MatSort} sort
   */
  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.conceptsContainer.conceptDataSource.sort = sort;
    this.setupSortChange();
  }


  conceptFields: Array<ConceptField>;
  columns: Array<TableColumn<Concept>>;
  displayedCol: string[] = [];
  currentConceptTypeId = null;

  private sortSub: Subscription;
  private _isLargeScreenSig; // initialized in constructor
  get isLargeScreen(): boolean { return this._isLargeScreenSig(); }

  constructor() {
    this._isLargeScreenSig = toSignal(
      this.breakpointObserver.observe('(min-width: 1280px)').pipe(
        map(state => state.matches)
      ),
      { initialValue: false }
    );
  }

  ngOnChanges() {
    this.setReloadConcepts(true).then(() => {
      this.setConceptFields().then();
    });
  }

  public ngOnDestroy(): void {
    if (this.sortSub && !this.sortSub.closed) {
      this.sortSub.unsubscribe();
    }
  }

  get height() {
    return window.innerHeight - 460;
  }


  async setReloadConcepts(resetStart, goBack?: boolean) {
    await this.conceptService.setConceptList(this.conceptsContainer, resetStart, goBack, this.savePageIndex);
    this.conceptsContainer.conceptDataSource.paginator.length = this.conceptsContainer.concepts.hits;
  }

  async changePage() {
    this.conceptsContainer.pageIndex = this.conceptsContainer.conceptDataSource.paginator.pageIndex;
    await this.setReloadConcepts(false);
  }

  selectAll() {
    this.conceptService.selectAll(this.conceptsContainer);
  }

  async openConcept(concept: {conceptId: string, goBack: boolean, pageIndex: number | null}) {
    if(!concept.goBack) {
      this.savePageIndex = concept.pageIndex
    }
    if (!this.conceptsContainer.loadingConcepts && this.conceptsContainer.conceptType.is_hierarchic) {
      this.conceptsContainer.conceptsParams.parent_id = concept.conceptId;
      await this.setReloadConcepts(true, concept.goBack);
    }
  }

  private setupSortChange() {
    if (this.conceptsContainer.conceptDataSource.sort) {
      if (this.sortSub && !this.sortSub.closed) {
        this.sortSub.unsubscribe();
      }
      this.sortSub = this.conceptsContainer.conceptDataSource.sort.sortChange.subscribe(() => {
        this.conceptsContainer.pageIndex = 0;
        this.setReloadConcepts(true).then();
      });
    }
  }

  private async setConceptFields() {
    const objectTypeNameFieldMap = {};
    objectTypeNameFieldMap[AConst.OBJECT_TYPE_AGENT] = 'agent_name';
    objectTypeNameFieldMap[AConst.OBJECT_TYPE_COLLECTION] = 'collection_name';
    objectTypeNameFieldMap[AConst.OBJECT_TYPE_NAMED_OBJECT] = 'named_object_name';

    const modelOverviewFields = await this.cms.getModelOverviewFields({
      modelName: this.conceptsContainer.conceptType.concept_type_id})
    this.conceptFields = [];
    const fieldSelect = new ConceptField(
      'select', 'TRANS__EDIT_FIELD__CHOOSE', 'select', false);
    this.conceptFields.push(fieldSelect);
    const conceptName = this.conceptsContainer.conceptType.concept_name;
    const conceptType = this.conceptsContainer.conceptType.concept_type_id;
    const nameField = objectTypeNameFieldMap[conceptType];
    if (nameField) {
      if (this.featureFlagsService.getFeatureFlags().temporary.newNameFields) {
        this.nameField = nameField;
      }
    }
    if (conceptName) {
      const fieldConceptName = new ConceptField(
        this.nameField, conceptName, 'name', true);
      this.conceptFields.push(fieldConceptName);
    }
    for (let field of modelOverviewFields) {
      this.conceptFields.push(this.metaFieldToConceptField(field));
    }
    const fieldAction = new ConceptField(
      'action', '', 'action', false);
    this.conceptFields.push(fieldAction);

    if (this.currentConceptTypeId !== this.conceptsContainer.conceptType.concept_type_id) {
      this.displayedCol = this.conceptFields.map(conceptField => conceptField.name);
      this.currentConceptTypeId = this.conceptsContainer.conceptType.concept_type_id;
    }
  }

  private metaFieldToConceptField(field: MetaField): ConceptField {
    let fieldName = field.field_name;
    if ([FieldType.MAP_ID.toString(), FieldType.MAP_ID_ARRAY.toString()].includes(field.field_type) &&
      (fieldName.endsWith('_id') || fieldName.endsWith('_ids'))) {
      if (fieldName.endsWith('_id')) {
        fieldName = `${fieldName}_value`;
      } else if (fieldName.endsWith('_ids')) {
        fieldName = `${fieldName}_values`;
      }
    }
    return new ConceptField(fieldName, field.title, this.mapType(field), true);
  }

  private mapType(field: MetaField): string {
    let res = 'value';
    switch (field.field_type) {
      case FieldType.BOOLEAN:
        res = 'check';
        break;
      case FieldType.DATE_TIME_ISO:
      case FieldType.DATE_ISO:
        res = 'datetime';
        break;
    }
    if (field.name === this.nameField) {
      res = 'name';
    }
    return res;
  }
}
